import { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/shared/AuthContext";
import { PageLoader } from "@/components/shared/PageLoader/PageLoader";
import { LoginPage } from "@/pages/LoginPage/LoginPage";
import PrivateRoute from "./PrivateRoute";
import { CertificateVerificationPage } from "@/components/admin/Learning/Certificates/CertificateVerificationPage";
import MainLayout from "@/layouts/MainLayout/MainLayout";
import { AdminRoutes } from "./AdminRoutes";
import { UserRoutes } from "./UserRoutes";
import { OAuthTestResultPage } from "@/pages/OAuthTestResultPage/OAuthTestResultPage";
import OAuthCallbackPage from "@/pages/OAuthCallbackPage/OAuthCallbackPage";

/** Legacy QR/link format (/user/verify-certificate?cert_id=...) → public page. */
const LegacyVerifyRedirect = () => {
  const [params] = useSearchParams();
  const certId = params.get("cert_id");
  return <Navigate to={certId ? `/verify/${certId}` : "/"} replace />;
};

const GuestDashboardPage = lazy(() =>
  import("@/pages/GuestDashboardPage/GuestDashboardPage").then(m => ({
    default: m.GuestDashboardPage,
  })),
);
const ExternalFormPage = lazy(() =>
  import("@/pages/Public/ExternalFormPage").then(m => ({ default: m.ExternalFormPage })),
);
const LegalPage = lazy(() =>
  import("@/pages/Public/LegalPage").then(m => ({ default: m.LegalPage })),
);
const PublicCatalogPage = lazy(() =>
  import("@/pages/PublicCatalog/PublicCatalogPage").then(m => ({ default: m.PublicCatalogPage })),
);
const PublicCoursePage = lazy(() =>
  import("@/pages/PublicCatalog/PublicCoursePage").then(m => ({ default: m.PublicCoursePage })),
);
/** Signed in, the public catalogue pages hand over to Explore and the course page. */
const SignedInCatalogRedirect = () => {
  const [params] = useSearchParams();
  const q = params.get("q");
  const type = params.get("type");
  // The public catalogue's types: communities have their own page; programs / live workshops are catalogue filters.
  if (type === "community") return <Navigate to="/user/communities" replace />;
  const p = new URLSearchParams();
  if (q) p.set("q", q);
  if (type === "program" || type === "seminar") p.set("type", type);
  const search = p.toString();
  return <Navigate to={search ? `/user/courses?${search}` : "/user/courses"} replace />;
};
const SignedInCourseRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/user/course-start/${id}`} replace />;
};
/** A free-preview activity (the public player); signed in, the course player opens it. */
const PublicCourseActivityPage = lazy(() =>
  import("@/pages/PublicCatalog/PublicCoursePage").then(m => ({ default: m.PublicCourseActivityPage })),
);
const SignedInActivityRedirect = () => {
  const { courseId, activityId } = useParams();
  return <Navigate to={`/user/course/${courseId}/activity/${activityId}`} replace />;
};
const ChangePasswordPage = lazy(() =>
  import("@/pages/ChangePasswordPage/ChangePasswordPage").then(m => ({ default: m.ChangePasswordPage })),
);

const AppRoutes = () => {
  const { isAuthenticated, isLoading, isImpersonating } = useAuth();

  if (isLoading) return <PageLoader fullScreen />;

  return (
    <Suspense fallback={<PageLoader fullScreen />}>
      <Routes>
        <Route
          path="/"
          element={
            isAuthenticated ? (
              isImpersonating ? (
                <Navigate to="/user/dashboard" replace />
              ) : (
                <Navigate to="/user/dashboard" replace />
              )
            ) : (
              <GuestDashboardPage />
            )
          }
        />

        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />
        <Route path="/auth/test-result" element={<OAuthTestResultPage />} />
        <Route path="/verify/:certId" element={<CertificateVerificationPage />} />
        <Route path="/user/verify-certificate" element={<LegacyVerifyRedirect />} />
        <Route path="/forms/external/:token" element={<ExternalFormPage />} />
        {/* Public (signed out too): Terms of Use / Privacy Policy / Cookie Policy */}
        <Route path="/legal/:kind" element={<LegalPage />} />
        {/* Public course catalogue (security setting "Show the course catalogue to visitors"). */}
        <Route path="/catalog" element={isAuthenticated ? <SignedInCatalogRedirect /> : <PublicCatalogPage />} />
        <Route path="/catalog/course/:id" element={isAuthenticated ? <SignedInCourseRedirect /> : <PublicCoursePage />} />
        <Route path="/catalog/course/:courseId/activity/:activityId" element={isAuthenticated ? <SignedInActivityRedirect /> : <PublicCourseActivityPage />} />
        {/* Signed in, outside the app shell: "Choose a new password" (forced after sign-in, or from the account menu) */}
        <Route path="/change-password" element={<ChangePasswordPage />} />


        <Route element={<PrivateRoute><MainLayout /></PrivateRoute>}>
          <Route path="user/*" element={<UserRoutes />} />
          <Route path="admin/*" element={<AdminRoutes />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;